import React, { useState, useEffect } from 'react';
import { X, Camera, Code, Layout, Menu, Monitor, Sun, Moon } from 'lucide-react';
import { useOperationalSettings } from '../../../contexts/OperationalSettingsContext';
import { RegistrationFormDisplay, OperationalSettings, LogLevel, Theme } from '../../../types/operationalSettings';
import { useKeycloak } from '../../../contexts/KeycloakContext';
import { logger } from '../../../services/logger';
import toast from 'react-hot-toast';

interface PersonalSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

type SettingSection = 'interface' | 'detection' | 'developer' | 'view';

export const PersonalSettingsModal: React.FC<PersonalSettingsModalProps> = ({ open, onClose }) => {
  const { settings, updateSettings } = useOperationalSettings();
  const { isSuperAdmin } = useKeycloak();
  const [localSettings, setLocalSettings] = useState<OperationalSettings>(settings);
  const [activeSection, setActiveSection] = useState<SettingSection>('interface');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Sync local settings when modal opens
  useEffect(() => {
    if (open) {
      setLocalSettings(settings);
      setActiveSection('interface');
      setIsSidebarOpen(false);
    }
  }, [open, settings]);

  const handleSave = () => {
    updateSettings(localSettings);

    // Update logger level immediately
    logger.setLogLevel(localSettings.logLevel);

    toast.success('Configurações salvas!');
    onClose();
  };

  const handleCancel = () => {
    // Reset to current settings
    setLocalSettings(settings);
    onClose();
  };

  const menuItems = [
    {
      id: 'interface' as SettingSection,
      icon: Layout,
      label: 'Interface',
      description: 'Formulários e visualização'
    },
    {
      id: 'detection' as SettingSection,
      icon: Camera,
      label: 'Detecção',
      description: 'Detecção facial'
    },
    ...(isSuperAdmin ? [{
      id: 'developer' as SettingSection,
      icon: Code,
      label: 'Desenvolvedor',
      description: 'Logs e debug'
    }] : []),
    ...(isSuperAdmin || localStorage.getItem('isOperationalMode') === 'true' ? [{
      id: 'view' as SettingSection,
      icon: Monitor,
      label: 'Visualização',
      description: 'Alternar modo de visão'
    }] : [])
  ];

  const handleSectionClick = (section: SettingSection) => {
    setActiveSection(section);
    setIsSidebarOpen(false); // Close mobile menu after selection
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'interface':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-200 mb-1">Interface</h3>
              <p className="text-sm text-gray-500">Configure como os formulários e elementos da interface aparecem</p>
            </div>

            {/* Registration Form Display Section */}
            <div className="space-y-4">
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-300">
                  Abrir formulários de cadastro em:
                </label>
                <p className="text-xs text-gray-500">
                  Escolha como os formulários de cadastro (visitantes, moradores) devem aparecer
                </p>
              </div>

              {/* Radio Group */}
              <div className="space-y-3">
                {/* Modal Option */}
                <label
                  className={`
                    flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all
                    ${
                      localSettings.registrationFormDisplay === 'modal'
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-gray-800 bg-gray-900 hover:border-gray-700'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="formDisplay"
                    value="modal"
                    checked={localSettings.registrationFormDisplay === 'modal'}
                    onChange={(e) => setLocalSettings({
                      ...localSettings,
                      registrationFormDisplay: e.target.value as RegistrationFormDisplay
                    })}
                    className="mt-0.5 h-4 w-4 text-blue-500 border-gray-700 focus:ring-blue-500 focus:ring-offset-0 focus:ring-offset-gray-950"
                  />
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-200">Modal (popup)</span>
                      {localSettings.registrationFormDisplay === 'modal' && (
                        <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full">
                          Padrão
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      Abre formulários em janelas separadas que sobrepõem o conteúdo atual. Permite visualizar dados em segundo plano.
                    </p>
                  </div>
                </label>

                {/* Main Frame Option */}
                <label
                  className={`
                    flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all
                    ${
                      localSettings.registrationFormDisplay === 'main-frame'
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-gray-800 bg-gray-900 hover:border-gray-700'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="formDisplay"
                    value="main-frame"
                    checked={localSettings.registrationFormDisplay === 'main-frame'}
                    onChange={(e) => setLocalSettings({
                      ...localSettings,
                      registrationFormDisplay: e.target.value as RegistrationFormDisplay
                    })}
                    className="mt-0.5 h-4 w-4 text-blue-500 border-gray-700 focus:ring-blue-500 focus:ring-offset-0 focus:ring-offset-gray-950"
                  />
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-200">Área principal</span>
                    </div>
                    <p className="text-xs text-gray-500">
                      Substitui o conteúdo principal temporariamente. Útil para fluxos de trabalho focados e telas menores.
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Device Connectivity */}
            <div className="space-y-3 pt-4 border-t border-gray-800">
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-300">Comunicação com dispositivos</label>
                <p className="text-xs text-gray-500">Configurações de rede para acesso direto a leitoras e câmeras</p>
              </div>
              <label className="flex items-start gap-3 p-4 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-750 transition-colors">
                <input
                  type="checkbox"
                  checked={localSettings.usePublicIpAsDirect ?? false}
                  onChange={(e) => setLocalSettings({
                    ...localSettings,
                    usePublicIpAsDirect: e.target.checked
                  })}
                  className="mt-1 h-4 w-4 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-200">Usar endereço público como local</div>
                  <p className="text-sm text-gray-400 mt-1">
                    Usa o IP público/nuvem do dispositivo para comunicação direta em vez do IP local.
                    Ative quando estiver fora da rede local (ex: via VPN externa ou acesso remoto).
                  </p>
                </div>
              </label>
            </div>

            {/* Theme */}
            <div className="space-y-3 pt-4 border-t border-gray-800">
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-300">Tema da interface</label>
                <p className="text-xs text-gray-500">Escolha entre tema escuro (padrão) ou claro</p>
              </div>
              <div className="flex gap-3">
                <label className={`flex-1 flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${localSettings.theme !== 'light' ? 'border-blue-500 bg-blue-500/10' : 'border-gray-800 bg-gray-900 hover:border-gray-700'}`}>
                  <input
                    type="radio"
                    name="theme"
                    value="dark"
                    checked={localSettings.theme !== 'light'}
                    onChange={() => setLocalSettings({ ...localSettings, theme: 'dark' as Theme })}
                    className="h-4 w-4 text-blue-500 border-gray-700 focus:ring-blue-500 focus:ring-offset-0"
                  />
                  <Moon className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-200">Escuro</span>
                </label>
                <label className={`flex-1 flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${localSettings.theme === 'light' ? 'border-blue-500 bg-blue-500/10' : 'border-gray-800 bg-gray-900 hover:border-gray-700'}`}>
                  <input
                    type="radio"
                    name="theme"
                    value="light"
                    checked={localSettings.theme === 'light'}
                    onChange={() => setLocalSettings({ ...localSettings, theme: 'light' as Theme })}
                    className="h-4 w-4 text-blue-500 border-gray-700 focus:ring-blue-500 focus:ring-offset-0"
                  />
                  <Sun className="h-4 w-4 text-yellow-400" />
                  <span className="text-sm font-medium text-gray-200">Claro</span>
                </label>
              </div>
            </div>

            {/* Layout: Botoeira / Eventos position */}
            <div className="space-y-3 pt-4 border-t border-gray-800">
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-300">Posição da Botoeira e Eventos</label>
                <p className="text-xs text-gray-500">Escolha onde a botoeira (acionamento de dispositivos) e o painel de eventos aparecem na tela</p>
              </div>
              <div className="flex gap-3">
                <label className={`flex-1 flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${localSettings.layout !== 'swapped' ? 'border-blue-500 bg-blue-500/10' : 'border-gray-800 bg-gray-900 hover:border-gray-700'}`}>
                  <input
                    type="radio"
                    name="layout"
                    value="default"
                    checked={localSettings.layout !== 'swapped'}
                    onChange={() => setLocalSettings({ ...localSettings, layout: 'default' })}
                    className="h-4 w-4 text-blue-500 border-gray-700 focus:ring-blue-500 focus:ring-offset-0"
                  />
                  <Layout className="h-4 w-4 text-gray-400" />
                  <div>
                    <span className="text-sm font-medium text-gray-200 block">Padrão</span>
                    <span className="text-xs text-gray-500">Botoeira embaixo, eventos à direita</span>
                  </div>
                </label>
                <label className={`flex-1 flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${localSettings.layout === 'swapped' ? 'border-blue-500 bg-blue-500/10' : 'border-gray-800 bg-gray-900 hover:border-gray-700'}`}>
                  <input
                    type="radio"
                    name="layout"
                    value="swapped"
                    checked={localSettings.layout === 'swapped'}
                    onChange={() => setLocalSettings({ ...localSettings, layout: 'swapped' })}
                    className="h-4 w-4 text-blue-500 border-gray-700 focus:ring-blue-500 focus:ring-offset-0"
                  />
                  <Layout className="h-4 w-4 text-gray-400 rotate-90" />
                  <div>
                    <span className="text-sm font-medium text-gray-200 block">Invertido</span>
                    <span className="text-xs text-gray-500">Botoeira à direita, eventos embaixo</span>
                  </div>
                </label>
              </div>
            </div>

            {/* Info Note */}
            <div className="flex gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <svg
                className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-xs text-gray-400">
                Você pode alterar esta configuração a qualquer momento através do menu de usuário.
              </p>
            </div>
          </div>
        );

      case 'detection':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-200 mb-1 flex items-center gap-2">
                <Camera className="h-5 w-5" />
                Detecção de Rosto
              </h3>
              <p className="text-sm text-gray-500">Configure como a detecção facial funciona ao carregar fotos</p>
            </div>

            <div className="space-y-3">
              <label className="flex items-start gap-3 p-4 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-750 transition-colors">
                <input
                  type="checkbox"
                  checked={localSettings.autoFaceDetection}
                  onChange={(e) => setLocalSettings({
                    ...localSettings,
                    autoFaceDetection: e.target.checked
                  })}
                  className="mt-1 h-4 w-4 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-200">
                    Detecção Automática
                  </div>
                  <p className="text-sm text-gray-400 mt-1">
                    Detecta e centraliza rostos automaticamente ao carregar fotos.
                    Desabilite para melhorar performance em ambientes com recursos limitados.
                  </p>
                </div>
              </label>
            </div>

            <div className="p-3 bg-amber-900/20 border border-amber-700/50 rounded-lg">
              <p className="text-xs text-amber-200">
                <strong>Nota:</strong> Quando desabilitado, você pode clicar no botão
                "Detectar Rosto" manualmente para usar a detecção quando necessário.
              </p>
            </div>
          </div>
        );

      case 'developer':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-200 mb-1 flex items-center gap-2">
                <Code className="h-5 w-5" />
                Configurações de Desenvolvedor
              </h3>
              <p className="text-sm text-gray-500">Configurações avançadas para depuração e desenvolvimento</p>
            </div>

            <div className="space-y-3">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-200">
                  Nível de Log do Console
                </label>
                <p className="text-xs text-gray-500">
                  Controla quais mensagens aparecem no console do navegador (F12)
                </p>
              </div>

              {/* Log Level Radio Group */}
              <div className="space-y-2">
                {/* Error (Default) */}
                <label className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-750 transition-colors">
                  <input
                    type="radio"
                    name="logLevel"
                    value="error"
                    checked={localSettings.logLevel === 'error'}
                    onChange={(e) => setLocalSettings({
                      ...localSettings,
                      logLevel: e.target.value as LogLevel
                    })}
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-600 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-200">Apenas Erros</span>
                      <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full">
                        Padrão
                      </span>
                    </div>
                    <p className="text-sm text-gray-400 mt-1">
                      Mostra apenas erros críticos. Recomendado para uso normal.
                    </p>
                  </div>
                </label>

                {/* Warning */}
                <label className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-750 transition-colors">
                  <input
                    type="radio"
                    name="logLevel"
                    value="warn"
                    checked={localSettings.logLevel === 'warn'}
                    onChange={(e) => setLocalSettings({
                      ...localSettings,
                      logLevel: e.target.value as LogLevel
                    })}
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-600 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-200">Avisos e Erros</div>
                    <p className="text-sm text-gray-400 mt-1">
                      Mostra avisos e erros. Útil para identificar problemas potenciais.
                    </p>
                  </div>
                </label>

                {/* Info */}
                <label className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-750 transition-colors">
                  <input
                    type="radio"
                    name="logLevel"
                    value="info"
                    checked={localSettings.logLevel === 'info'}
                    onChange={(e) => setLocalSettings({
                      ...localSettings,
                      logLevel: e.target.value as LogLevel
                    })}
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-600 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-200">Informações</div>
                    <p className="text-sm text-gray-400 mt-1">
                      Mostra informações gerais, avisos e erros. Bom para monitoramento.
                    </p>
                  </div>
                </label>

                {/* Debug */}
                <label className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-750 transition-colors">
                  <input
                    type="radio"
                    name="logLevel"
                    value="debug"
                    checked={localSettings.logLevel === 'debug'}
                    onChange={(e) => setLocalSettings({
                      ...localSettings,
                      logLevel: e.target.value as LogLevel
                    })}
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-600 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-200">Debug (Verbose)</div>
                    <p className="text-sm text-gray-400 mt-1">
                      Mostra TODAS as mensagens. Útil para debugging profundo.
                    </p>
                  </div>
                </label>

                {/* None */}
                <label className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-750 transition-colors">
                  <input
                    type="radio"
                    name="logLevel"
                    value="none"
                    checked={localSettings.logLevel === 'none'}
                    onChange={(e) => setLocalSettings({
                      ...localSettings,
                      logLevel: e.target.value as LogLevel
                    })}
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-600 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-200">Desabilitado</div>
                    <p className="text-sm text-gray-400 mt-1">
                      Não mostra nenhuma mensagem no console.
                    </p>
                  </div>
                </label>
              </div>
            </div>

            <div className="p-3 bg-blue-900/20 border border-blue-700/50 rounded-lg">
              <p className="text-xs text-blue-200">
                <strong>Dica:</strong> Ative "Debug" quando estiver investigando problemas específicos.
                Lembre-se de voltar para "Apenas Erros" depois para evitar poluição do console.
              </p>
            </div>
          </div>
        );

      case 'view':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-200 mb-1 flex items-center gap-2">
                <Monitor className="h-5 w-5" />
                Visualização
              </h3>
              <p className="text-sm text-gray-500">Alternar entre os modos de visão disponíveis</p>
            </div>

            <div className="p-4 bg-gray-800 rounded-lg border border-gray-700">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <Monitor className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-200">Modo Operacional</span>
                      <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full">Ativo</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Visão otimizada para operações de portaria com monitoramento em tempo real.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    onClose();
                    localStorage.setItem('isOperationalMode', 'false');
                    window.location.reload();
                  }}
                  className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md font-medium bg-gray-700 hover:bg-gray-600 text-gray-200 transition-colors flex-shrink-0"
                >
                  <Monitor className="h-3 w-3" />
                  Visão Normal
                </button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/80 z-50 animate-fade-in"
        onClick={handleCancel}
      />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-gray-950 border border-gray-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-800 flex-shrink-0">
            <div className="flex items-center gap-3">
              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="lg:hidden p-2 hover:bg-gray-800 rounded-lg transition-colors"
                title="Menu"
              >
                <Menu className="h-5 w-5 text-gray-400" />
              </button>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-200">Configurações Pessoais</h2>
            </div>
            <button
              onClick={handleCancel}
              className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors"
              title="Fechar"
            >
              <X className="h-5 w-5 text-gray-400" />
            </button>
          </div>

          {/* Content Area - macOS Style Split */}
          <div className="flex-1 flex overflow-hidden min-h-0">
            {/* Left Sidebar - macOS Style */}
            <div className={`
              ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
              lg:translate-x-0
              absolute lg:relative inset-y-0 left-0 z-20
              w-56 bg-gray-900 border-r border-gray-800
              transition-transform duration-200 ease-in-out
              flex flex-col flex-shrink-0
            `}>
              {/* Mobile Sidebar Header */}
              <div className="lg:hidden flex items-center justify-between p-4 border-b border-gray-800">
                <span className="text-sm font-medium text-gray-400">Menu</span>
                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className="p-1 hover:bg-gray-800 rounded transition-colors"
                >
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              </div>

              {/* Menu Items */}
              <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeSection === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleSectionClick(item.id)}
                      className={`
                        w-full flex items-start gap-3 p-3 rounded-lg
                        transition-all duration-200
                        ${isActive
                          ? 'bg-blue-500/10 border border-blue-500/30 text-blue-400'
                          : 'hover:bg-gray-800 text-gray-400 hover:text-gray-200'
                        }
                      `}
                    >
                      <Icon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${isActive ? 'text-blue-400' : 'text-gray-500'}`} />
                      <div className="flex-1 text-left">
                        <div className={`text-sm font-medium ${isActive ? 'text-blue-400' : 'text-gray-200'}`}>
                          {item.label}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {item.description}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </nav>

              {/* Sidebar Footer */}
              <div className="p-3 border-t border-gray-800">
                <div className="text-xs text-gray-500 text-center">
                  Preferências do usuário
                </div>
              </div>
            </div>

            {/* Mobile Sidebar Overlay */}
            {isSidebarOpen && (
              <div
                className="lg:hidden absolute inset-0 bg-black/50 z-10"
                onClick={() => setIsSidebarOpen(false)}
              />
            )}

            {/* Main Content Area */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-4 sm:p-6 max-w-3xl">{renderContent()}</div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 sm:p-6 border-t border-gray-800 flex-shrink-0 bg-gray-950">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-lg shadow-blue-500/20"
            >
              Salvar
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
